import { Snackbar, Alert } from '@mui/material';
import type { SnackbarOrigin } from '@mui/material/Snackbar';
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ReactComponent as WarningIcon } from '../assets/images/WarningIcon.svg';

type ToastType = 'success' | 'error' | 'info';

type ToastOptions = {
    origin?: SnackbarOrigin;
    action?: (close: () => void) => React.ReactNode;
    icon?: React.ReactNode;
    /**
     * Device-switch banner styling; the number is how many lines of message to
     * show before truncating.
     */
    banner?: 1 | 2;
    autoHideMs?: number;
};

type ShowToast = (message: string, options?: ToastOptions) => string;

type ToastContextValue = {
    success: ShowToast;
    error: ShowToast;
    info: ShowToast;
    close: (id: string) => void;
};

export const ToastContext = createContext<ToastContextValue>({
    success: () => '',
    error: () => '',
    info: () => '',
    close: () => {},
});

type ToastMessage = {
    id: string;
    type: ToastType;
    message: string;
    options: ToastOptions;
    open: boolean;
};

const defaultOptions: ToastOptions = {
    origin: {
        horizontal: 'center',
        vertical: 'top',
    },
};

const backgroundColors: Record<ToastType, string> = {
    success: '#007d3e',
    error: '#bd362d',
    info: '#015ebb',
};

const defaultIcon = (type: ToastType): React.ReactNode => {
    if (type === 'success') {
        return <CheckCircleIcon sx={{ marginRight: '2px' }} />;
    }
    if (type === 'error') {
        return <WarningIcon style={{ marginRight: '2px' }} />;
    }
    return undefined;
};

export const ToastProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
    const [messages, setMessages] = useState<ToastMessage[]>([]);
    const messageCount = useRef(0);

    const close = useCallback((messageId: string) => {
        setMessages((prevMessages) => prevMessages.filter((message) => message.id !== messageId));
    }, []);

    const show = useCallback((type: ToastType, message: string, options?: ToastOptions) => {
        messageCount.current += 1;

        const newMessage: ToastMessage = {
            id: `${Date.now()}-${messageCount.current}`,
            type,
            message,
            options: { ...defaultOptions, ...options },
            open: true,
        };
        setMessages((prevMessages) => [...prevMessages, newMessage]);

        return newMessage.id;
    }, []);

    const success: ShowToast = useCallback(
        (message, options) => show('success', message, options),
        [show],
    );

    const error: ShowToast = useCallback(
        (message, options) => show('error', message, options),
        [show],
    );

    const info: ShowToast = useCallback(
        (message, options) => show('info', message, options),
        [show],
    );

    const value = useMemo(() => ({ success, error, info, close }), [success, error, info, close]);

    return (
        <ToastContext.Provider value={value}>
            {children}
            {messages.map((message) => (
                <Snackbar
                    key={message.id}
                    open={message.open}
                    autoHideDuration={message.options.autoHideMs ?? null}
                    onClose={message.options.action ? undefined : () => close(message.id)}
                    anchorOrigin={message.options.origin}
                    slotProps={{
                        root: {
                            style: {},
                        },
                    }}
                >
                    <Alert
                        variant="filled"
                        sx={{
                            borderRadius: '12px',
                            backgroundColor: backgroundColors[message.type],
                            alignItems: 'center',
                            ...(message.options.banner && {
                                maxWidth: '400px',
                                padding: '10px 16px',
                                '& .MuiAlert-icon': { padding: 0, alignItems: 'center' },
                                '& .MuiAlert-message': {
                                    padding: 0,
                                    fontWeight: 600,
                                    lineHeight: '24px',
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: message.options.banner,
                                    overflow: 'hidden',
                                },
                            }),
                            ...(message.options.action && {
                                '& .MuiAlert-action': { padding: '0 0 0 12px', marginRight: 0 },
                            }),
                        }}
                        severity={message.type}
                        action={message.options.action?.(() => close(message.id))}
                        icon={message.options.icon ?? defaultIcon(message.type)}
                        onClose={() => close(message.id)}
                    >
                        {message.message}
                    </Alert>
                </Snackbar>
            ))}
        </ToastContext.Provider>
    );
};

export const ToastConsumer = ToastContext.Consumer;

export const useToast = () => useContext(ToastContext);
